import React, { Suspense, useEffect } from 'react';

import { QueryClientProvider } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { Outlet } from 'react-router';
import { ObservabilityProvider } from '@lotlinx-aws/observability-react';
import { DealerSelectorProvider } from '@lotlinx-aws/dealer-selector';

import styles from 'App.module.scss';
import ChatbotHost from 'components/ChatbotHost';
import ErrorFallback from 'components/ErrorFallback/ErrorFallback';
import ErrorModal from 'components/ErrorModal/ErrorModal';
import ErrorToastListener from 'components/ErrorToastListener/ErrorToastListener';
import SideNavbar from 'components/SideNavbar/SideNavbar';
import TopNavbar from 'components/TopNavbar';
import useClickTracker from 'hooks/useClickTracker';
import useLDUserTracker from 'hooks/useLDUserTracker';
import useRouteTitle from 'hooks/useRouteTitle';
import useSyncDealerFavorites from 'hooks/useSyncDealerFavorites';
import { queryClient } from 'queryClient';
import ROUTES from 'routes';
import { userAtom } from 'store';
import { ToastProvider } from '@lotlinx-aws/sparky-component-library/Sparky/Toast';
import { AccountType } from 'types/User';
const MarketMaps = React.lazy(() => import('pages/MarketMaps'));
const PIIModalRefactor = React.lazy(() => import('components/PiiSession/PIIModal/PIIModal'));
const PiiSessionCountdownSync = React.lazy(
  () => import('components/PiiSession/PiiSessionCountdownSync'),
);
const ReportsGenerationModal = React.lazy(() => import('components/ReportsGenerationModal'));

const ReactQueryDevtools = import.meta.env.DEV
  ? React.lazy(() =>
      import('@tanstack/react-query-devtools').then((mod) => ({ default: mod.ReactQueryDevtools })),
    )
  : () => null;

// Always light for light mode from component library
document.documentElement.setAttribute('data-theme', 'light');

export default function App() {
  const [user] = useAtom(userAtom);
  const title = useRouteTitle(ROUTES);

  useLDUserTracker();

  //* Make the account information available in the inspector console to be brought into data layer for GTM
  useEffect(() => {
    if (user) {
      /* @ts-ignore */
      window.getVisitorInfo = { visitor: { id: user?.id, email: user?.email } };
    }
  }, [user]);

  useEffect(() => {
    document.title = title;
  }, [title]);

  return (
    <ObservabilityProvider
      user={user}
      errorFallback={(props) => (
        <ErrorFallback error={props.error} resetErrorBoundary={props.resetError} />
      )}
    >
      <QueryClientProvider client={queryClient}>
        <Suspense>
          <ReactQueryDevtools position='bottom' />
        </Suspense>
        <ToastProvider>
          <DealerSelectorProvider
            apiUrl={import.meta.env.VITE_APP_API_URL ?? ''}
            isInternalUser={user?.accountType === AccountType.OWNER}
            token={user?.token}
            appId='Turn4'
          >
            <ChatbotHost>
              <AuthenticatedApp />
            </ChatbotHost>
          </DealerSelectorProvider>
          <ErrorModal />
          <ErrorToastListener />
        </ToastProvider>
      </QueryClientProvider>
    </ObservabilityProvider>
  );
}

/**
 * Routes for authenticated users
 */
function AuthenticatedApp() {
  const [user] = useAtom(userAtom);

  useClickTracker();
  useSyncDealerFavorites(user?.token);

  return (
    <div className={styles.main}>
      <div className={styles.topNav}>
        <TopNavbar />
      </div>
      <div className={styles.sideNav}>
        <SideNavbar />
      </div>
      <div className={styles.content}>
        <div className={styles.page}>
          <Outlet key={user?.token} />
          <Suspense fallback={null}>
            <ReportsGenerationModal />
          </Suspense>
        </div>
      </div>
      <Suspense fallback={null}>
        <MarketMaps />
        <PiiSessionCountdownSync />
        <PIIModalRefactor />
      </Suspense>
    </div>
  );
}
